/*
  Присланные файлы - таблица варианта Б: что за файл, что в нём нашли, сколько
  счетов, где он сейчас. Удалить можно каждый: вместе с ним уходят сведения, не
  перенесённые в портфель.

  Лист без лицевого счёта называется прямо - «не с чем связать»: ключ
  сопоставления один, и угадывать счёт по ФИО или адресу система не будет
  (решение владельца 30.09.2026).
*/

import { date, facts, UPLOAD_STATE_TITLES } from '../../api/format.js'
import { Lock } from '../AccessContext.jsx'
import { Empty, Loading, Notice, Tag } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import TransferLock, { LOCK_TITLE } from './TransferLock.jsx'
import useFilesPage from './useFilesPage.js'

const LAYOUT_TITLES = {
  table: 'таблица',
  wide_charges: 'таблица',
  onec_report: 'отчёт 1С по начислениям',
  onec_accounts: 'отчёт 1С по счетам',
  scan: 'скан',
  empty: 'пустой',
}

const DOCUMENT_TITLES = { docx: 'документ Word', pdf: 'документ PDF' }

// Вид документа - по расширению: у договора без таблиц листов нет вовсе, и
// «таблица» по умолчанию была бы неправдой (осмотр 30.09.2026).
function kindOf(file) {
  const layout = file.sheets[0]?.layout
  const suffix = file.filename.split('.').pop().toLowerCase()
  if (layout === 'scan') return LAYOUT_TITLES.scan
  return DOCUMENT_TITLES[suffix] ?? LAYOUT_TITLES[layout] ?? 'таблица'
}

const STATE_TONE = { read: 'calm', received: 'muted', needs_review: 'warn', failed: 'burn' }

function describe(sheet, state) {
  // Скан сведений до сверки не даёт: их называет человек, сверив страницы.
  if (sheet.layout === 'scan') {
    const done = state === 'read' ? 'сверен' : 'сведения появятся после сверки'
    return `${sheet.name.toLowerCase()} - ${done}`
  }
  if (!sheet.has_key) return 'не с чем связать - нет лицевого счёта'
  return facts(sheet.facts) || 'только номера счетов'
}

function Found({ sheets, hasSettings, state }) {
  const keyed = sheets.filter((sheet) => sheet.has_key)
  // Карточка предприятия или перечень домов - не данные должников: сказать
  // «не с чем связать» о них было бы неправдой, их сведения показаны выше.
  const shown = hasSettings ? keyed : sheets
  return (
    <>
      {hasSettings ? <div>сведения организации - показаны выше</div> : null}
      {shown.map((sheet) => (
        <div key={sheet.name || 'лист'}>
          {sheets.length > 1 ? <span className="muted">{sheet.name}: </span> : null}
          {describe(sheet, state)}
        </div>
      ))}
    </>
  )
}

/*
  `locked` - идёт перенос в портфель (01.10.2026, проверка перед выкладкой):
  удалить файл посреди переноса значило бы выдернуть сведения, которые перенос
  сейчас пишет, - сервер отказывает (409), а кнопка выключается заранее.
*/
/*
  С 21.10.2026 строки таблицы режет сервер (требование владельца: пагинация серверная):
  таблица сама просит окно файлов (`useFilesPage`) и перечитывает его, когда вкладка
  сообщает об изменении (`stamp`). Число в заголовке - общее число файлов у сервера, а не
  длина показанной страницы.
*/
export default function UploadTable({ stamp, onDelete, onReview, busy, locked = false }) {
  // Хук - до раннего return: первая страница приходит позже первого показа.
  const { rows, total, error, bar } = useFilesPage(stamp)
  if (rows === null) {
    return (
      <section className="section">
        <Notice error={error} />
        {error ? null : <Loading what="Загружаем присланные файлы" />}
      </section>
    )
  }
  if (total === 0) {
    return (
      <section className="section">
        <Empty title="Файлов ещё нет">
          Пришлите реестр, отчёт по начислениям или любую таблицу с лицевыми счетами - здесь
          появится, что в каждом файле нашлось и чего не хватает.
        </Empty>
      </section>
    )
  }
  return (
    <section className="section">
      <h2 className="section__title">Присланные файлы · {total}</h2>
      <Notice error={error} />
      <TransferLock locked={locked} />
      <div className="card">
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Файл</th>
                <th>Вид</th>
                <th>Что нашли</th>
                <th className="table__num">Счетов</th>
                <th>Состояние</th>
                <th>Хранится</th>
                <th aria-label="Действие" />
              </tr>
            </thead>
            <tbody>
              {rows.map((file) => (
                <tr key={file.id}>
                  <td className="filename">{file.filename}</td>
                  <td data-label="Вид">{kindOf(file)}</td>
                  <td className="table__block" data-label="Что нашли">
                    <Found sheets={file.sheets} hasSettings={file.has_settings} state={file.state} />
                    {file.error_message ? <div className="muted">{file.error_message}</div> : null}
                  </td>
                  <td className="table__num" data-label="Счетов">
                    {file.accounts_total?.toLocaleString('ru-RU') ?? '-'}
                  </td>
                  <td data-label="Состояние">
                    <Tag kind={STATE_TONE[file.state] ?? 'muted'}>
                      {UPLOAD_STATE_TITLES[file.state] ?? file.state}
                    </Tag>
                  </td>
                  <td data-label="Хранится">
                    {/* Файл, давший сведения счёту, уборка не трогает (решение
                        владельца 01.10.2026): дата здесь обещала бы удаление. */}
                    {file.kept_with_accounts ? 'пока счёт в портфеле' : `до ${date(file.expires_at)}`}
                  </td>
                  <td data-label="Действие">
                    {file.sheets[0]?.layout === 'scan' && file.state !== 'received' ? (
                      <button
                        type="button"
                        className={file.state === 'needs_review' ? 'button' : 'button button--quiet'}
                        disabled={busy}
                        onClick={() => onReview(file)}
                      >
                        {file.state === 'needs_review' ? 'Сверить' : 'Открыть сверку'}
                      </button>
                    ) : null}
                    <Lock>
                      <button
                        type="button"
                        className="button button--quiet"
                        disabled={busy || locked}
                        title={locked ? LOCK_TITLE : undefined}
                        onClick={() => onDelete(file)}
                      >
                        Удалить
                      </button>
                    </Lock>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PageBar {...bar} what="файлов" />
      </div>
    </section>
  )
}
